import {
  type Expr,
  type TypedExpr,
  type SoQLType,
  type Scope,
  type UnAnalyzedAst,
  type BinaryTree,
  type TypedSelect,
} from 'common/types/soql';
import SoqlDataProvider from 'common/visualizations/dataProviders/SoqlDataProvider';
import { getSuggestions, getSuggestionsNA, type Suggester } from '../../lib/data-helpers';
import { getUnAnalyzedAst, getAstFromAnalysis } from '../../lib/selectors';
import { type AppState } from '../../redux/store';
import _ from 'lodash';
import { default as React } from 'react';
import { connect } from 'react-redux';
import { none, type Option } from 'ts-option';
import { type PhxChannel } from 'common/types/dsmapi';
import { ForgeAutocomplete, ForgeChipField, ForgeChip, ForgeIcon } from '@tylertech/forge-react';
import { type IAutocompleteOption } from '@tylertech/forge';
import { fetchTranslation } from 'common/locale';
import { whichAnalyzer } from 'common/explore_grid/lib/feature-flag-helpers';
import { Either, factorOption } from 'common/either';
import { ClientContextVariable } from 'common/types/clientContextVariable';
import { getReplacing } from '../../redux/actions';

const t = (k: string) => fetchTranslation(k, 'shared.explore_grid.visual_nodes');

type ExternalProps = {
  update: (newVals: string[]) => void;
  suggestionTarget: Either<Expr, TypedExpr>;
  soqlT: Option<SoQLType>;
  literals: string[];
  scope: Scope;
};

type Props = ExternalProps & {
  queryContext: {
    clientContextInfo: { variables: ClientContextVariable[] };
    replacing: string | null;
  };
  fourfour: string;
  tree: Option<Either<BinaryTree<UnAnalyzedAst>, BinaryTree<TypedSelect>>>;
  channel: PhxChannel;
};
class VariadicMultiSelect extends React.Component<Props> {
  suggester: Option<Suggester> = none;
  suggestionTarget: Props['suggestionTarget'];

  constructor(props: Props) {
    super(props);
    this.buildSuggester(props);
  }

  buildSuggester = (props: Props) => {
    const { queryContext, fourfour, scope, channel, suggestionTarget } = props;

    const provider = new SoqlDataProvider({
      domain: window.location.host,
      datasetUid: fourfour,
    });

    this.suggester = this.props.tree.map((eitherTree) => {
      return eitherTree.fold(
        (tree) => getSuggestions(provider, suggestionTarget.left, scope, tree, channel),
        (tree) => {
          const { replacing, clientContextInfo } = queryContext;
          return getSuggestionsNA(
            provider,
            suggestionTarget.right,
            tree,
            clientContextInfo,
            replacing,
            channel,
          );
        },
      );
    });
    this.suggestionTarget = props.suggestionTarget;
  };

  onAdd = (literal: string) => {
    this.props.update(_.uniq([...this.props.literals, literal]));
  };

  onRemove = (literal?: string) => {
    if (literal !== undefined && literal !== null) {
      this.props.update(this.props.literals.filter((l) => l !== literal));
    }
  };

  fetchSuggestions = (filterText: string): Promise<IAutocompleteOption<string>[]> => {
    if (!_.isEqual(this.suggestionTarget, this.props.suggestionTarget)) {
      this.buildSuggester(this.props);
    }

    const noResults: IAutocompleteOption[] = [
      {
        label: t('no_results'),
        value: null,
        disabled: true,
      },
    ];

    return this.suggester
      .map((suggest) =>
        suggest(filterText, 0, true).then((suggestions) => {
          const options = suggestions.results.map((res) => ({ label: res.title, value: res.title }));
          return options.length === 0 ? noResults : options;
        }),
      )
      .getOrElseValue(Promise.resolve([]));
  };

  multiSelect() {
    return (
      <div id="dsmui-tags-input" className="multi-select-visual-node">
        <ForgeAutocomplete
          mode="stateless"
          filter={this.fetchSuggestions}
          on-forge-autocomplete-select={(evt: CustomEvent) => {
            this.onAdd(evt.detail.value);
          }}
          on-forge-chip-field-member-added={(evt: CustomEvent) => {
            this.onAdd(evt.detail);
          }}
          filterOnFocus={true}
          allowUnmatched={true}
          filterFocusFirst={false}
        >
          <ForgeChipField className="multi-select-chip-field">
            {this.props.literals.map((chipTag: string) => {
              return (
                <ForgeChip
                  key={chipTag}
                  slot="member"
                  type="field"
                  dense
                  on-forge-chip-delete={(evt: CustomEvent) => this.onRemove(evt.detail.value)}
                  value={chipTag}
                >
                  {chipTag}
                </ForgeChip>
              );
            })}
            <ForgeIcon slot="leading" name="search" />
            <label slot="label" htmlFor="chip-field-input">
              {t('search')}
            </label>
            <input autoComplete="off" type="text" id="chip-field-input" />
            <ForgeIcon slot="trailing" data-forge-dropdown-icon name="arrow_drop_down" />
          </ForgeChipField>
        </ForgeAutocomplete>
      </div>
    );
  }

  render() {
    return (
      <div className="function-arg variadic-multi-select" data-testid="variadic-multi-select">
        <div className="literal string-literal-with-suggestions">{this.multiSelect()}</div>
      </div>
    );
  }
}

const mapStateToProps = (state: AppState, props: ExternalProps): Props => {
  const queryContext = {
    clientContextInfo: state.clientContextInfo,
    replacing: getReplacing(state),
  };
  return {
    ...props,
    queryContext,
    fourfour: state.fourfourToQuery,
    tree: factorOption<BinaryTree<UnAnalyzedAst>, BinaryTree<TypedSelect>>(
      whichAnalyzer(getUnAnalyzedAst, getAstFromAnalysis)(state.query),
    ),
    channel: state.channel,
  };
};

export default connect(mapStateToProps)(VariadicMultiSelect);
